// src/app/CadreAgent.jsx — LE CADRE DE L'ESPACE AGENT DE MÉNAGE, d'après la
// maquette NEST v677 (barre latérale « x124Lat », tête et barre du bas du
// téléphone). Il remplace, pour le seul rôle ménage, la barre du haut et la
// barre du bas communes : les autres rôles gardent les leurs.
//
//   · ordinateur : barre latérale de 232 px — NEST, « Mon espace », les deux
//     onglets, la pastille du compte en pied ;
//   · téléphone : une tête (marque, « Sofia · agent de ménage », pastille) et
//     la barre du bas. Aucun bleu.
//
// ⚠️ La feuille « Mon compte » se limite à ce que l'agent peut déjà faire
// (qui il est, Déconnexion) : Mes infos, Mot de passe et Mes accès demandent des
// portes de droits (couloir F, « Comptes et accès »).

import { useEffect, useState } from 'react'
import { CalendarDays, Star, ChevronRight, LogOut } from 'lucide-react'
import { supabase } from '../supabase'
import { LOGO_NEST } from '../assets/logo-nest'
import useEtroit from '../lib/useEtroit'
import { firstNameFromEmail } from '../views/menage/RunCard'
import { FeuilleAgent } from '../views/menage/CartePlanning'
import { NEST, POLICE } from '../views/menage/palette-nest'

const ICONES = { 'menage-today': CalendarDays, 'mon-travail': Star }
const FOND = '#F8FAF9'

const libelle = (c) => String(c.label).replace(/^[^A-Za-zÀ-ÿ]+\s*/, '')

function Marque({ taille = 36 }) {
  return (
    <span aria-hidden="true" style={{ width: taille, height: taille, borderRadius: 10, flexShrink: 0, background: `#fff url("${LOGO_NEST}") center/cover no-repeat`,
      border: `1px solid ${NEST.bordCarte}` }} />
  )
}

function Compte({ n, testId, style }) {
  return (
    <span data-testid={testId} style={{ minWidth: 18, height: 18, padding: '0 5px', boxSizing: 'border-box', borderRadius: 9, background: NEST.aub, color: '#fff',
      fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...style }}>{n}</span>
  )
}

function Pastille({ initiales }) {
  return (
    <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 10, background: '#12302A', color: '#fff', fontSize: 12, fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initiales}</span>
  )
}

export default function CadreAgent({ currentUser, categories, activeId, onChoisir, onDeconnexion }) {
  const etroit = useEtroit()
  const [compteOuvert, setCompteOuvert] = useState(false)
  const [prenomSaisi, setPrenomSaisi] = useState(null)
  useEffect(() => {
    let annule = false
    supabase.auth.getSession().then(({ data }) => {
      const p = data?.session?.user?.user_metadata?.first_name
      if (!annule) setPrenomSaisi(typeof p === 'string' && p.trim() ? p.trim() : null)
    }).catch((e) => { console.error('[cadre-agent] session', e?.message || 'erreur') })
    return () => { annule = true }
  }, [currentUser?.email])
  // La pastille de « Mon planning » : ce qui reste à faire, dit par la journée.
  const [aFaire, setAFaire] = useState(null)
  useEffect(() => {
    const ecouter = (e) => setAFaire(e.detail)
    window.addEventListener('fbz:agent-a-faire', ecouter)
    return () => window.removeEventListener('fbz:agent-a-faire', ecouter)
  }, [])
  const pastilleDe = (c) => (c.id === 'menage-today' && aFaire > 0 ? aFaire : null)
  const prenom = prenomSaisi || firstNameFromEmail(currentUser?.email) || 'Mon compte'
  const initiales = prenom.slice(0, 2).toUpperCase()

  const compte = compteOuvert && (
    <FeuilleAgent titre={prenom} testId="feuille-compte-agent" onFermer={() => setCompteOuvert(false)}>
      <div style={{ background: NEST.fondCarte, borderRadius: 12, padding: 12, fontSize: 12.5, color: NEST.gris, marginBottom: 12 }}>
        Agent de ménage · {currentUser?.email}
      </div>
      <button type="button" data-testid="agent-deconnexion" onClick={onDeconnexion}
        style={{ width: '100%', minHeight: 44, borderRadius: 10, border: `1px solid ${NEST.boutonBord}`, background: '#fff', color: NEST.aubTexte,
          fontFamily: POLICE, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
        <LogOut size={15} strokeWidth={2} /> Déconnexion
      </button>
    </FeuilleAgent>
  )

  if (etroit) {
    return (
      <>
        <header data-testid="agent-tete" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderBottom: `1px solid ${NEST.bordCarte}`,
          padding: '10px 16px', fontFamily: POLICE }}>
          <Marque />
          <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: NEST.grisFonce, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prenom} · agent de ménage</span>
          <button type="button" data-testid="agent-compte" aria-label="Mon compte" title="Mon compte" onClick={() => setCompteOuvert(true)}
            style={{ width: 44, height: 44, border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Pastille initiales={initiales} />
          </button>
        </header>
        <style>{`
          @media (max-width: 860px){ body{padding-bottom: calc(66px + env(safe-area-inset-bottom))} }
          body.fbz-voile-ouvert [data-testid="barre-onglets-bas"]{display:none !important}
        `}</style>
        <nav data-testid="barre-onglets-bas" aria-label="Navigation principale"
          style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 80, background: NEST.fondCarte, borderTop: `1px solid ${NEST.bordCarte}`,
            display: 'flex', gap: 4, padding: '5px 5px calc(6px + env(safe-area-inset-bottom))', fontFamily: POLICE }}>
          {categories.map((c) => {
            const actif = c.id === activeId
            const Icone = ICONES[c.id] || Star
            return (
              <button key={c.id} type="button" data-testid={`onglet-bas-${c.id}`} aria-current={actif ? 'page' : undefined} onClick={() => onChoisir(c.id)}
                style={{ position: 'relative', flex: 1, minHeight: 54, border: 'none', borderRadius: 12, background: actif ? '#fff' : 'transparent', boxShadow: actif ? '0 1px 4px rgba(23,33,30,.12)' : 'none',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer', fontFamily: POLICE }}>
                <Icone size={20} strokeWidth={2} color={NEST.encre} aria-hidden="true" />
                {pastilleDe(c) && <Compte n={pastilleDe(c)} testId={`onglet-bas-badge-${c.id}`} style={{ position: 'absolute', top: 4, right: 'calc(50% - 26px)' }} />}
                <span style={{ fontSize: 11, fontWeight: actif ? 700 : 600, color: NEST.encre }}>{libelle(c)}</span>
              </button>
            )
          })}
        </nav>
        {compte}
      </>
    )
  }

  return (
    <>
      <style>{`@media (min-width: 861px){ .fbz-app{padding-left:232px} }`}</style>
      <aside data-testid="agent-laterale" style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: 232, boxSizing: 'border-box', zIndex: 80, background: FOND,
        borderRight: `1px solid ${NEST.bordCarte}`, display: 'flex', flexDirection: 'column', padding: '14px 12px', fontFamily: POLICE, color: NEST.encre }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 6px 18px' }}>
          <Marque /><span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '.06em' }}>NEST</span>
        </div>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: NEST.gris, padding: '0 8px 8px' }}>Mon espace</span>
        <nav aria-label="Navigation principale" style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
          {categories.map((c) => {
            const actif = c.id === activeId
            const Icone = ICONES[c.id] || Star
            return (
              <button key={c.id} type="button" data-testid={`agent-onglet-${c.id}`} aria-current={actif ? 'page' : undefined} onClick={() => onChoisir(c.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 42, padding: '0 12px', border: 'none', borderRadius: 10, textAlign: 'left',
                  background: actif ? NEST.vertPale : 'transparent', color: NEST.encre, fontFamily: POLICE, fontSize: 13, fontWeight: actif ? 700 : 500, cursor: 'pointer' }}>
                <Icone size={18} strokeWidth={2} aria-hidden="true" /> <span style={{ flex: 1 }}>{libelle(c)}</span>
                {pastilleDe(c) && <Compte n={pastilleDe(c)} testId={`agent-badge-${c.id}`} />}
              </button>
            )
          })}
        </nav>
        <button type="button" data-testid="agent-compte" title="Mon compte" onClick={() => setCompteOuvert(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 10, background: '#fff', border: `1px solid ${NEST.bordCarte}`, borderRadius: 12,
            fontFamily: POLICE, textAlign: 'left', cursor: 'pointer', color: NEST.encre }}>
          <Pastille initiales={initiales} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: 13 }}>{prenom}</b>
            <span style={{ display: 'block', fontSize: 11, color: NEST.gris, lineHeight: 1.3 }}>Mon compte · agent de ménage</span>
          </span>
          <ChevronRight size={16} strokeWidth={2} color={NEST.gris} aria-hidden="true" />
        </button>
      </aside>
      {compte}
    </>
  )
}
